import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@material-ui/core";
import { FieldValidationError, ValidationHelper } from "../../../services/server/ServerResponses";
import { BuilderAssetService } from "../../../services/BuilderAssetService";
import { useLibrary, useLibraryAlert } from "../LibraryPage";
import { convertBuilderAssetToLibraryItem } from "../../../entities/BuilderAsset";
import { LibraryItem } from "../../../entities/LibraryItem";
import { LibraryItemType } from "../../../entities/LibraryItemType";
import { convertFileAssetToLibraryItem } from "../../../entities/FileAsset";
import { convertFolderToLibraryItem } from "../../../entities/Folder";
import { convertLinkAssetToLibraryItem } from "../../../entities/LinkAsset";
import { convertSeriesToLibraryItem } from "../../../entities/SermonSeries";
import { FileAssetService } from "../../../services/FIleAssetService";
import { FolderService } from "../../../services/FolderService";
import { LinkAssetService } from "../../../services/LinkAssetService";
import { SermonSeriesService } from "../../../services/SermonSeriesService";

interface Props {
    focusedItem: LibraryItem;
    open: boolean;
    onClose: () => void;
}

export function RenameAssetDialog(props:Props){
    const libraryAlert = useLibraryAlert();
    const context = useLibrary();
    const { focusedItem } = props;

    const [newTitle, setNewTitle] = useState(focusedItem.name);
    const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
    const [disabled, setDisabled] = useState(false);

    const handleRename = async(e:React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const trimmedTitle = newTitle.trimEnd();
        if(trimmedTitle.length === 0)
            return setValidationErrors([{field: "Name", errors: ["Name cannot be blank"]}])

        switch(focusedItem.type) {
			case LibraryItemType.Folder:
			case LibraryItemType.PremiumSermon:
			case LibraryItemType.PremiumSermonSeries:
				return await handleFolderRename(trimmedTitle);
			case LibraryItemType.SermonSeries:
				return await handleSeriesRename(trimmedTitle);
			case LibraryItemType.BuilderAsset:
				return await handleAssetRename(trimmedTitle);
			case LibraryItemType.FileAsset:
				return await handleFileRename(trimmedTitle);
			case LibraryItemType.LinkAsset:
				return await handleLinkRename(trimmedTitle);
			default:
				return;
		}
    }

    const handleFolderRename = async(title:string) => {
        setDisabled(true)
        const response = await FolderService.setFolderName(focusedItem.id, title)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.updateLibraryItem(convertFolderToLibraryItem(response.data))
            libraryAlert(`${focusedItem.name} renamed to ${title}`, async () => {
                const result = await FolderService.setFolderName(focusedItem.id, focusedItem.name);
                if(result.success) {
                    context.updateLibraryItem(convertFolderToLibraryItem(result.data))
                    libraryAlert(`${newTitle} renamed to ${focusedItem.name}`);
                } else {
                    libraryAlert(`Failed to rename ${newTitle}`);
                }
            });
            props.onClose();
        }
    }

    const handleSeriesRename = async(title: string) => {
        setDisabled(true)
        const response = await SermonSeriesService.setNameSeries(focusedItem.id, title)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.updateLibraryItem(convertSeriesToLibraryItem(response.data))
            libraryAlert(`${focusedItem.name} renamed to ${title}`, async () => {
                const result = await SermonSeriesService.setNameSeries(focusedItem.id, focusedItem.name);
                if(result.success) {
                    context.updateLibraryItem(convertSeriesToLibraryItem(result.data))
                    libraryAlert(`${newTitle} renamed to ${focusedItem.name}`);
                } else {
                    libraryAlert(`Failed to rename ${newTitle}`);
                }
            });
            props.onClose();
        }
    }

    const handleAssetRename = async(title:string) => {
        setDisabled(true)
        const response = await BuilderAssetService.setName(focusedItem.id, title)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.updateLibraryItem(convertBuilderAssetToLibraryItem(response.data))
            libraryAlert(`${focusedItem.name} renamed to ${title}`, async () => {
                const result = await BuilderAssetService.setName(focusedItem.id, focusedItem.name);
                if(result.success) {
                    context.updateLibraryItem(convertBuilderAssetToLibraryItem(result.data))
                    libraryAlert(`${newTitle} renamed to ${focusedItem.name}`);
                } else {
                    libraryAlert(`Failed to rename ${newTitle}`);
                }
            });
            props.onClose();
        }
    }

    const handleFileRename = async(title:string) => {
        setDisabled(true)
        const response = await FileAssetService.setName(focusedItem.id, title)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.updateLibraryItem(convertFileAssetToLibraryItem(response.data))
            libraryAlert(`${focusedItem.name} renamed to ${title}`, async () => {
                const result = await FileAssetService.setName(focusedItem.id, focusedItem.name);
                if(result.success) {
                    context.updateLibraryItem(convertFileAssetToLibraryItem(result.data))
                    libraryAlert(`${newTitle} renamed to ${focusedItem.name}`);
                } else {
                    libraryAlert(`Failed to rename ${newTitle}`);
                }
            });
            props.onClose();
        }
    }

    const handleLinkRename = async(title:string) => {
        setDisabled(true)
        const response = await LinkAssetService.setName(focusedItem.id, title)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.updateLibraryItem(convertLinkAssetToLibraryItem(response.data))
            libraryAlert(`${focusedItem.name} renamed to ${title}`, async () => {
                const result = await LinkAssetService.setName(focusedItem.id, focusedItem.name);
                if(result.success) {
                    context.updateLibraryItem(convertLinkAssetToLibraryItem(result.data))
                    libraryAlert(`${newTitle} renamed to ${focusedItem.name}`);
                } else {
                    libraryAlert(`Failed to rename ${newTitle}`);
                }
            });
            props.onClose();
        }
    }

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            aria-labelledby="rename-form-dialog-title"
            disableBackdropClick={disabled}
            disableEscapeKeyDown={disabled}
        >
            <DialogTitle id="rename-form-dialog-title">Rename</DialogTitle>
            <form onSubmit={handleRename}>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="renameField"
                        label="Name"
                        type="text"
                        disabled={disabled}
                        style={{ minWidth: "400px" }}
                        fullWidth
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.currentTarget.value)}
                        error={ValidationHelper.isFieldInError(validationErrors, "Name")}
                        helperText={<span>{ValidationHelper.getFieldErrorSummary(validationErrors, "Name") }</span>}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onClose} disabled={disabled} color="primary">
                        Cancel
                        </Button>
                    <Button color="primary" disabled={disabled} type="submit">
                        Ok
                        </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}